// src/components/StateChangesDebugPanel.tsx
import {
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { Filter, Info, Trash2 } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';

import type { DeviceConfigurationType } from '@model/DeviceConfiguration';
import { useHeartRateStore } from '@store/useHeartRateStore';
import type { ExposureEvent, Phase } from '@types';
import { formatTimestampToTime } from '../utils/formatDuration';
import {
  findPortataNumber,
  formatExposureItemEventDetail,
  getExposureItemEventTypeLabel,
  type ExposureFoodDisplayContext,
} from '../utils/exposureMultiItem';
import { getExposureEventItemIds } from '../utils/normalizeExposureEvent';

interface StateChangesDebugPanelProps {
  onClear?: () => void;
  sessionType?: DeviceConfigurationType;
  exposureFoodDisplay?: ExposureFoodDisplayContext;
}

type FilterType = 'all' | 'heartRate' | 'stateChanges';

// Componente ottimizzato per le righe della tabella
const TableRow = React.memo<{
  item: {
    timestamp: number;
    type: 'heartRate' | 'stateChange';
    phase?: Phase;
    hr: number;
    hrv?: number;
    lf?: number;
    hf?: number;
  };
  index: number;
  getStateColor: (state: Phase) => string;
}>(({ item, index, getStateColor }) => (
  <Table.Tr key={`${item.type}-${item.timestamp}-${index}`}>
    <Table.Td>
      <Text size='xs'>{formatTimestampToTime(item.timestamp)}</Text>
    </Table.Td>
    <Table.Td>
      {item.type === 'stateChange' && item.phase ? (
        <Badge color={getStateColor(item.phase)} size='xs'>
          {item.phase}
        </Badge>
      ) : (
        <Text size='xs' c='dimmed'>
          HR
        </Text>
      )}
    </Table.Td>
    <Table.Td>
      <Text size='xs' c='dimmed'>
        {item.hr}{' '}
        <Text size='10px' c='dimmed' span>
          (bpm)
        </Text>
      </Text>
    </Table.Td>
    <Table.Td>
      <Text size='xs' c='dimmed'>
        {item.hrv ? (
          <>
            {item.hrv}{' '}
            <Text size='10px' c='dimmed' span>
              (ms)
            </Text>
          </>
        ) : (
          '--'
        )}
      </Text>
    </Table.Td>
    <Table.Td>
      <Text size='xs' c='dimmed'>
        {item.lf ? (
          <>
            {item.lf}{' '}
            <Text size='10px' c='dimmed' span>
              (ms²)
            </Text>
          </>
        ) : (
          '--'
        )}
      </Text>
    </Table.Td>
    <Table.Td>
      <Text size='xs' c='dimmed'>
        {item.hf ? (
          <>
            {item.hf}{' '}
            <Text size='10px' c='dimmed' span>
              (ms²)
            </Text>
          </>
        ) : (
          '--'
        )}
      </Text>
    </Table.Td>
  </Table.Tr>
));

const getExposureEventLabel = (
  event: ExposureEvent,
  isMultiItem: boolean
): string => {
  switch (event.type) {
    case 'tutorialCompleted':
      return 'Tutorial completato';
    case 'itemShown':
      return getExposureItemEventTypeLabel('itemShown', isMultiItem);
    case 'itemFeedback':
      return getExposureItemEventTypeLabel('itemFeedback', isMultiItem);
    case 'sessionStartedAt':
      return 'Inizio sessione';
    case 'sessionEndedAt':
      return 'Fine sessione';
    case 'mealEnded':
      return 'Pasto terminato';
    default:
      return String((event as { type: string }).type);
  }
};

/** Restituisce l’array itemIds anche per eventi persistiti con foodIds/foodId legacy. */
function getExposureItemIds(event: {
  type: string;
  itemIds?: number[];
  foodIds?: number[];
  foodId?: number;
}): number[] {
  return getExposureEventItemIds(event);
}

const getExposureEventDetail = (
  event: ExposureEvent,
  display: ExposureFoodDisplayContext
): string => {
  switch (event.type) {
    case 'itemShown':
    case 'itemFeedback': {
      const itemIds = getExposureItemIds(event);
      const portataNumber = display.isMultiItem
        ? findPortataNumber(display.portate, itemIds)
        : null;
      return formatExposureItemEventDetail({
        eventType: event.type,
        itemIds,
        portataNumber,
        isMultiItem: display.isMultiItem,
        score: event.type === 'itemFeedback' ? event.score : undefined,
      });
    }
    case 'mealEnded':
      return event.clientId ?? '--';
    default:
      return '--';
  }
};

const defaultExposureFoodDisplay: ExposureFoodDisplayContext = {
  isMultiItem: false,
  portate: [],
};

const StateChangesPanel: React.FC<StateChangesDebugPanelProps> = ({
  onClear,
  sessionType,
  exposureFoodDisplay = defaultExposureFoodDisplay,
}) => {
  const [filter, setFilter] = React.useState<FilterType>('all');
  const events = useHeartRateStore(state => state.events);
  const phaseEvents = useHeartRateStore(state => state.phaseEvents);
  const exposureEvents = useHeartRateStore(state => state.exposureEvents);
  // const baseline = useHeartRateStore(state => state.baseline);
  // const phase = useHeartRateStore(state => state.phase);

  const getStateColor = useCallback((state: Phase) => {
    switch (state) {
      case 'p0':
        return 'gray';
      case 'p1':
        return 'blue';
      case 'p2':
        return 'red';
      case 'p-1':
        return 'cyan';
      case 'p-2':
        return 'purple';
      default:
        return 'gray';
    }
  }, []);

  // Combinare i dati in base al filtro selezionato
  const combinedData = useMemo(() => {
    const data: Array<{
      timestamp: number;
      type: 'heartRate' | 'stateChange';
      phase?: Phase;
      hr: number;
      hrv?: number;
      lf?: number;
      hf?: number;
    }> = [];

    if (filter === 'all' || filter === 'heartRate') {
      // Validazione: assicurati che events sia un array
      if (Array.isArray(events)) {
        events.forEach((point, index) => {
          // Usa il timestamp reale se disponibile, altrimenti usa il timestamp numerico
          const timestamp = point.timestamp || Date.now() + index * 1000;

          data.push({
            timestamp,
            type: 'heartRate',
            hr: point.hr,
            hrv: point.hrv ?? undefined,
            lf: point.lf ?? undefined,
            hf: point.hf ?? undefined,
          });
        });
      }
    }

    if (filter === 'all' || filter === 'stateChanges') {
      phaseEvents.forEach(change => {
        data.push({
          timestamp: change.timestamp,
          type: 'stateChange',
          phase: change.phase,
          hr: change.hr || 0,
        });
      });
    }

    // Ordinare per timestamp
    return data.sort((a, b) => a.timestamp - b.timestamp);
  }, [events, phaseEvents, filter]);

  const totalCount = useMemo(() => {
    const heartRateLength = Array.isArray(events) ? events.length : 0;
    const stateChangesLength = Array.isArray(phaseEvents)
      ? phaseEvents.length
      : 0;

    switch (filter) {
      case 'heartRate':
        return heartRateLength;
      case 'stateChanges':
        return stateChangesLength;
      default:
        return heartRateLength + stateChangesLength;
    }
  }, [events, phaseEvents, filter]);

  if (sessionType === 'exposure') {
    const sortedExposure = [...exposureEvents].sort(
      (a, b) => b.timestamp - a.timestamp
    );
    return (
      <div>
        <Stack gap='md'>
          <Group justify='space-between' align='center' mb='xs' mih='100%'>
            <Text size='sm' fw={500}>
              Elenco Stati ({sortedExposure.length})
            </Text>
            {onClear && (
              <Button
                variant='light'
                color='red'
                size='xs'
                leftSection={<Trash2 size={14} />}
                onClick={onClear}
              >
                Pulisci
              </Button>
            )}
          </Group>
          {sortedExposure.length === 0 ? (
            <Text size='xs' c='dimmed' ta='center' py='md'>
              Nessun dato disponibile
            </Text>
          ) : (
            <div style={{ maxHeight: '500px', overflow: 'auto' }}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Orario</Table.Th>
                    <Table.Th>Tipo evento</Table.Th>
                    <Table.Th>Dettaglio</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedExposure.map((event, index) => (
                    <Table.Tr key={`${event.type}-${event.timestamp}-${index}`}>
                      <Table.Td>
                        <Text size='xs'>
                          {formatTimestampToTime(event.timestamp)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant='light' size='xs'>
                          {getExposureEventLabel(
                            event,
                            exposureFoodDisplay.isMultiItem
                          )}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='xs' c='dimmed'>
                          {getExposureEventDetail(event, exposureFoodDisplay)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
          <Card withBorder p='xs' bg='blue.0'>
            <Group gap='xs'>
              <Info size={16} color='blue' />
              <Text size='xs' c='blue'>
                Gli eventi Exposure (tutorial, cibi mostrati, feedback) vengono
                registrati durante la sessione.
              </Text>
            </Group>
          </Card>
        </Stack>
      </div>
    );
  }

  return (
    <div>
      <Stack gap='md'>
        {/* Storico Cambi di Stato */}

        <Group justify='space-between' align='center' mb='xs' mih={'100%'}>
          <Text size='sm' fw={500}>
            Storico Dati ({totalCount})
          </Text>
          {onClear && (
            <Button
              variant='light'
              color='red'
              size='xs'
              leftSection={<Trash2 size={14} />}
              onClick={onClear}
            >
              Pulisci
            </Button>
          )}
        </Group>

        {/* Filtri */}
        <Group mb='xs' align='center' gap='xs'>
          <Filter size={16} color='gray' style={{ verticalAlign: 'middle' }} />

          <SegmentedControl
            value={filter}
            onChange={value => setFilter(value as FilterType)}
            data={[
              { label: 'Tutti', value: 'all' },
              { label: 'Heart Rate', value: 'heartRate' },
              { label: 'Cambi Stato', value: 'stateChanges' },
            ]}
            size='xs'
          />
        </Group>

        {combinedData.length === 0 ? (
          <Text size='xs' c='dimmed' ta='center' py='md'>
            Nessun dato disponibile
          </Text>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Orario</Table.Th>
                  <Table.Th>Stato</Table.Th>
                  <Table.Th>HR (bpm)</Table.Th>
                  <Table.Th>HRV (ms)</Table.Th>
                  <Table.Th>LF (ms²)</Table.Th>
                  <Table.Th>HF (ms²)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {combinedData.map((item, index) => (
                  <TableRow
                    key={`${item.type}-${item.timestamp}-${index}`}
                    item={item}
                    index={index}
                    getStateColor={getStateColor}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}

        {/* Informazioni */}
        <Card withBorder p='xs' bg='blue.0'>
          <Group gap='xs'>
            <Info size={16} color='blue' />
            <Text size='xs' c='blue'>
              I cambi di stato vengono rilevati automaticamente quando il
              battito cardiaco varia oltre le soglie configurate rispetto alla
              baseline.
            </Text>
          </Group>
        </Card>
      </Stack>
    </div>
  );
};

// Ottimizzazione con React.memo per evitare re-render inutili
export default React.memo(StateChangesPanel);
